import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {I18nManager} from 'react-native';
import AppConstants from '../Constants/AppConstants';
import {getValuesFromCookies, saveCookies} from '../HelperClasses/UserPrefrences';
import {messages, supportedLocales} from './catalogs';

const LocaleContext = createContext(null);
const FALLBACK_LOCALE = 'en';
const LOCALE_TAGS = {
  en: 'en-US',
  fr: 'fr-FR',
  ar: 'ar',
  es: 'es-ES',
  pt: 'pt-BR',
  ta: 'ta-IN',
  ru: 'ru-RU',
  id: 'id-ID',
  it: 'it-IT',
  tr: 'tr-TR',
};

export const getLocaleTag = locale => LOCALE_TAGS[locale] || locale || 'en-US';

const normalizeLocale = locale => {
  const normalized = String(locale || FALLBACK_LOCALE).toLowerCase().split(/[-_]/)[0];
  return supportedLocales.some(item => item.code === normalized)
    ? normalized
    : FALLBACK_LOCALE;
};

const interpolate = (message, values) => {
  if (typeof message !== 'string' || !values || typeof values !== 'object') {
    return message;
  }
  return message.replace(/\{([a-zA-Z0-9_]+)\}/g, (placeholder, name) =>
    values[name] === undefined || values[name] === null
      ? placeholder
      : String(values[name]),
  );
};

export const LocaleProvider = ({children}) => {
  const reportedMissing = useRef(new Set());
  const [locale, setLocaleState] = useState(() =>
    normalizeLocale(getValuesFromCookies('appLanguage')),
  );

  const setLocale = useCallback(nextLocale => {
    const next = normalizeLocale(nextLocale);
    setLocaleState(next);
    AppConstants.appLanguage = next;
    saveCookies('appLanguage', next);
  }, []);

  useEffect(() => {
    AppConstants.appLanguage = locale;
    if (typeof document !== 'undefined') {
      document.documentElement.lang = getLocaleTag(locale);
      document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
      if (document.body) document.body.dir = locale === 'ar' ? 'rtl' : 'ltr';
    }
    I18nManager.allowRTL(locale === 'ar');
    I18nManager.forceRTL(locale === 'ar');
  }, [locale]);

  const translate = useCallback((key, paramsOrFallback, fallback) => {
    const params = paramsOrFallback && typeof paramsOrFallback === 'object'
      ? paramsOrFallback
      : undefined;
    const source = params
      ? (fallback === undefined ? key : fallback)
      : (paramsOrFallback === undefined ? key : paramsOrFallback);
    if (typeof key !== 'string' || key.length === 0) return source;
    const translated = messages[locale]?.[key] || messages.en?.[key];
    if (!translated && locale !== FALLBACK_LOCALE && process.env.NODE_ENV !== 'production') {
      const missingKey = `${locale}:${key}`;
      if (!reportedMissing.current.has(missingKey)) {
        reportedMissing.current.add(missingKey);
        if (reportedMissing.current.size === 1) {
          console.warn(`[localization] The ${locale} catalog has untranslated UI messages.`);
        }
      }
    }
    return interpolate(translated || source, params);
  }, [locale]);

  const formatDate = useCallback((value, options) => {
    if (value === null || value === undefined || value === '') return value;
    const dateValue = value instanceof Date
      ? value
      : typeof value === 'number' || /^\d+$/.test(String(value))
      ? Number(value)
      : value;
    const date = dateValue instanceof Date
      ? dateValue
      : typeof dateValue === 'number'
      ? new Date(dateValue < 1000000000000 ? dateValue * 1000 : dateValue)
      : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(getLocaleTag(locale), options).format(date);
  }, [locale]);

  const formatNumber = useCallback((value, options) =>
    new Intl.NumberFormat(getLocaleTag(locale), options).format(value), [locale]);

  const formatCurrency = useCallback((amount, currency, fallback) => {
    if (amount === null || amount === undefined || amount === '') return fallback ?? amount;
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return fallback ?? amount;
    if (/^[A-Za-z]{3}$/.test(String(currency || ''))) {
      try {
        return new Intl.NumberFormat(getLocaleTag(locale), {
          style: 'currency',
          currency: String(currency).toUpperCase(),
        }).format(numericAmount);
      } catch (error) {
        // Fall back to the API's currency label if it isn't an ISO currency code.
      }
    }
    const number = new Intl.NumberFormat(getLocaleTag(locale), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
    return `${currency || ''}${number}`;
  }, [locale]);

  const contextValue = useMemo(() => ({
    locale,
    setLocale,
    translate,
    formatDate,
    formatNumber,
    formatCurrency,
    supportedLocales,
  }), [locale, setLocale, translate, formatDate, formatNumber, formatCurrency]);

  return <LocaleContext.Provider value={contextValue}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    return {
      locale: FALLBACK_LOCALE,
      setLocale: () => {},
      translate: (key, paramsOrFallback, fallback) => {
        const params = paramsOrFallback && typeof paramsOrFallback === 'object'
          ? paramsOrFallback
          : undefined;
        const source = params
          ? (fallback === undefined ? key : fallback)
          : (paramsOrFallback === undefined ? key : paramsOrFallback);
        return interpolate(source, params);
      },
      formatDate: value => value,
      formatNumber: value => String(value),
      formatCurrency: (value, currency, fallback) => fallback ?? `${currency || ''}${value}`,
      supportedLocales,
    };
  }
  return context;
};

export const withLocale = WrappedComponent => {
  const LocalizedComponent = React.forwardRef((props, ref) => {
    const localeContext = useContext(LocaleContext);
    return <WrappedComponent {...props} localeContext={localeContext} ref={ref} />;
  });
  LocalizedComponent.displayName = `withLocale(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return LocalizedComponent;
};

export default LocaleContext;
